/**
 * Per the `stage-livestudio` OpenSpec change: the `StageScene` data
 * model for the operator's 3D AI-livestream backstage. A scene
 * describes a room a character is staged in — the character can walk
 * to / sit / lie on named anchors, and the broadcast view is captured
 * by OBS as a browser source. Operator-only; never fan-facing.
 *
 * Three room kinds (`360-pano`, `2d-backdrop`, `3d-room`) share ONE
 * `walkable` + `anchors` + `cameraPresets` structure so the runtime
 * Navigator/Driver never branch on `kind`; only the backdrop renderer
 * does. The `kind` discriminates which backdrop block is required —
 * enforced by `superRefine` below.
 *
 * Storage: DDB `nx-stages-{env}`, PK `customerId`, SK `stageId`
 * (operator-scoped list is a single-partition query). 360 panoramas are
 * operator-UPLOADED in v1 (generated on Skybox's own tool); NOXOR does
 * not call any 360-generation API server-side in v1.
 */
import { z } from 'zod';
/** A 3-tuple world coordinate (metres), `[x, y, z]`. */
const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
export const StageKindEnum = z.enum(['360-pano', '2d-backdrop', '3d-room']);
/**
 * A named position in the room the character can be sent to. `clip` is
 * the AnimationClip to play on arrival (e.g. a sit / lie / idle clip);
 * `facing` is the heading (degrees) the character turns to once there.
 */
export const StageAnchorSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(80),
    worldPos: Vec3Schema,
    /** Clip label to play on arrival. Resolved against the character's
     *  animationMap / stageClips at runtime. */
    clip: z.string().max(80).default('Idle'),
    /** Heading in degrees [-180, 180] the character faces on arrival. */
    facing: z.number().min(-180).max(180).default(0),
});
/**
 * A camera pose the broadcast view can cut to. Exactly one preset per
 * scene is marked `isDefault` (applied on load). An optional `motion`
 * adds a slow looping dolly/orbit so the shot isn't dead-static.
 */
export const CameraPresetSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(80),
    position: Vec3Schema,
    lookAt: Vec3Schema,
    isDefault: z.boolean().default(false),
    motion: z
        .object({
        kind: z.enum(['dolly', 'orbit']),
        /** Slow by design — keep viewers comfortable. Units are kind-specific
         *  (dolly: metres/sec; orbit: degrees/sec). Clamped small. */
        speed: z.number().min(0).max(15).default(3),
    })
        .optional(),
});
/**
 * A prop placed in the room (8.2). v1 stores a model URL + transform;
 * the operator picks these from the shared object-library. Mounted via
 * the character controller's scene-object pipeline as a static world
 * object.
 */
export const StageObjectSchema = z.object({
    id: z.string().min(1).max(64),
    /** Optional library object id this was placed from (for re-resolve). */
    libraryObjectId: z.string().max(64).optional(),
    label: z.string().max(80).optional(),
    modelUrl: z.string().url(),
    modelFormat: z.enum(['glb', 'fbx']).default('glb'),
    position: Vec3Schema.default([0, 0, 0]),
    /** Euler XYZ in degrees (converted to radians at mount). */
    rotation: Vec3Schema.default([0, 0, 0]),
    scale: Vec3Schema.default([1, 1, 1]),
    /** stage-broadcast-space — optional collision body so the character
     *  can't walk through this prop (walls/tables). Absent = decorative
     *  (passable, today's behaviour). `box` = XZ half-extents + the
     *  object's own yaw (oriented box); `circle` = radius. `auto` marks a
     *  body the engine derived from the model footprint (operator may then
     *  fine-tune). */
    collision: z
        .object({
        shape: z.enum(['box', 'circle']),
        halfExtents: z.tuple([z.number().min(0.01).max(50), z.number().min(0.01).max(50)]).optional(),
        radius: z.number().min(0.05).max(50).optional(),
        auto: z.boolean().optional(),
    })
        .optional(),
});
/**
 * stage-broadcast-space — a named broadcast zone (主播區 / 熱舞區 …). A
 * semantic destination region (circle on the floor) the AI director moves
 * to with PURPOSE. Distinct from anchors (precise points): a zone is an
 * area + intent. Optional on-arrival defaults apply when the character
 * reaches it.
 */
export const StageZoneSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(40),
    /** Centre on the floor (xz world units). */
    center: z.tuple([z.number(), z.number()]),
    /** Radius (m) of the circular region. */
    radius: z.number().min(0.2).max(30).default(1.5),
    /** Free-form purpose hint shown to the director ("唱跳表演", "讀觀眾留言"…). */
    intent: z.string().max(120).optional(),
    /** stage-broadcast-space — optional accent colour (hex, e.g. "#f472b6")
     *  used by the minimap, on-stage ring, spotlight and entry caption so
     *  zones read distinctly. Absent → default cyan. */
    color: z.string().max(16).optional(),
    /** Optional semantic action played on arrival (resolves like playAction). */
    onArrivalAction: z.string().max(80).optional(),
    /** Optional camera preset id cut to on arrival. */
    onArrivalCamera: z.string().max(64).optional(),
});
/** stage-broadcast-space — per-scene locomotion clip overrides. Defaults
 *  come from the character record's `stageClips`; these let one scene bind
 *  different clips WITHOUT touching the character. The ONLY world-overridable
 *  settings (see project_model_world_fidelity_principle). */
export const StageLocomotionSchema = z.object({
    idle: z.string().max(80).optional(),
    walk: z.string().max(80).optional(),
    run: z.string().max(80).optional(),
    jump: z.string().max(80).optional(),
    sit: z.string().max(80).optional(),
});
/** Polygon (xz-plane) the character is allowed to move within. */
export const WalkableSchema = z.object({
    polygon: z.array(z.tuple([z.number(), z.number()])).min(3),
});
/**
 * A no-go zone on the floor (8.3) — furniture / walls the character must
 * not walk through. v1 is a circle (centre xz + radius); the navigator
 * pushes the character out of any obstacle it would step into. Flat-pano
 * has no depth so these are operator-authored; a splat room (Phase 7)
 * can derive them from geometry.
 */
export const StageObstacleSchema = z.object({
    id: z.string().min(1).max(64),
    x: z.number(),
    z: z.number(),
    radius: z.number().min(0.05).max(10).default(0.5),
});
/** `360-pano` backdrop: an equirectangular image viewed from a fixed
 *  camera origin; the character moves within `movementRadius` of it. */
export const PanoramaBlockSchema = z.object({
    equirectUrl: z.string().url(),
    /** v2: depth map for parallax. Optional; absent = flat sphere (v1). */
    depthMapUrl: z.string().url().optional(),
    cameraOrigin: Vec3Schema,
    movementRadius: z.number().min(0.1).max(5).default(1),
});
/** `2d-backdrop`: a flat image behind the character; camera tuned so
 *  the floor line of the image matches the character's ground plane. */
export const BackdropBlockSchema = z.object({
    imageUrl: z.string().url(),
    cameraFov: z.number().min(10).max(120).default(35),
    cameraYaw: z.number().min(-180).max(180).default(0),
});
/** `3d-room`: a full GLB room. Anchors may come from named empties in
 *  the GLB OR the scene `anchors` array (array wins if present). */
export const Room3dBlockSchema = z.object({
    glbUrl: z.string().url(),
    navmeshUrl: z.string().url().optional(),
});
/**
 * The full stage scene. `ownerCustomerId` is server-stamped from the
 * authorizer — never trusted from the client write body (see the
 * Create/Update body schemas below which omit it).
 */
export const StageSceneSchema = z
    .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    ownerCustomerId: z.string().min(1),
    kind: StageKindEnum,
    panorama: PanoramaBlockSchema.optional(),
    backdrop: BackdropBlockSchema.optional(),
    room3d: Room3dBlockSchema.optional(),
    walkable: WalkableSchema,
    /** Floor height (world Y) — lets the operator raise/lower the whole
     *  walk plane + character + markers to sit on the room photo's floor.
     *  Default 0. Persisted so a saved scene restores the height. */
    groundY: z.number().min(-5).max(5).default(0),
    /** No-go zones (furniture / walls) the character must not enter. */
    obstacles: z.array(StageObstacleSchema).max(32).default([]),
    anchors: z.array(StageAnchorSchema).max(32).default([]),
    cameraPresets: z.array(CameraPresetSchema).max(12).default([]),
    /** Props placed in the room from the object library. */
    objects: z.array(StageObjectSchema).max(32).default([]),
    /** stage-broadcast-space — named broadcast zones (主播區/熱舞區…). */
    zones: z.array(StageZoneSchema).max(16).default([]),
    /** stage-broadcast-space — optional smaller live-roaming polygon; when
     *  present the character is clamped to THIS during broadcast instead of
     *  the full `walkable`. Absent = use full walkable (today's behaviour). */
    broadcastRange: WalkableSchema.optional(),
    /** stage-broadcast-space — per-scene locomotion clip overrides
     *  (idle/walk/run/jump/sit). Defaults to the character's stageClips. */
    locomotion: StageLocomotionSchema.optional(),
    /** Character bound to this stage by default. Optional — the console
     *  can override at mount time. */
    defaultCharacterId: z.string().max(64).optional(),
    // ai-live-director 2.1 — the livestream plan + accumulated memory
    // travel WITH the scene (cross-device; localStorage was Phase 1).
    directorScript: z.string().max(4000).optional(),
    directorMemory: z.array(z.string().max(200)).max(30).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
})
    .superRefine((s, ctx) => {
    // The backdrop block must match the declared kind, and the other
    // two blocks must be absent — the Navigator/Driver are kind-agnostic
    // but the renderer needs exactly the right block.
    const requireBlock = (present, field) => {
        if (!present) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} block required when kind="${s.kind}"`,
            });
        }
    };
    const rejectBlock = (present, field) => {
        if (present) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} block must be absent when kind="${s.kind}"`,
            });
        }
    };
    if (s.kind === '360-pano') {
        requireBlock(s.panorama, 'panorama');
        rejectBlock(s.backdrop, 'backdrop');
        rejectBlock(s.room3d, 'room3d');
    }
    else if (s.kind === '2d-backdrop') {
        requireBlock(s.backdrop, 'backdrop');
        rejectBlock(s.panorama, 'panorama');
        rejectBlock(s.room3d, 'room3d');
    }
    else {
        requireBlock(s.room3d, 'room3d');
        rejectBlock(s.panorama, 'panorama');
        rejectBlock(s.backdrop, 'backdrop');
    }
    // At most one default camera preset.
    const defaults = s.cameraPresets.filter((p) => p.isDefault);
    if (defaults.length > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cameraPresets'],
            message: 'at most one cameraPreset may be marked isDefault',
        });
    }
});
/** Create body — client supplies everything except server-owned fields. */
export const StageSceneCreateBodySchema = z
    .object({
    name: z.string().min(1).max(120),
    kind: StageKindEnum,
    panorama: PanoramaBlockSchema.optional(),
    backdrop: BackdropBlockSchema.optional(),
    room3d: Room3dBlockSchema.optional(),
    walkable: WalkableSchema,
    groundY: z.number().min(-5).max(5).default(0),
    obstacles: z.array(StageObstacleSchema).max(32).default([]),
    anchors: z.array(StageAnchorSchema).max(32).default([]),
    cameraPresets: z.array(CameraPresetSchema).max(12).default([]),
    objects: z.array(StageObjectSchema).max(32).default([]),
    zones: z.array(StageZoneSchema).max(16).default([]),
    broadcastRange: WalkableSchema.optional(),
    locomotion: StageLocomotionSchema.optional(),
    defaultCharacterId: z.string().max(64).optional(),
    // ai-live-director 2.1 — the show travels with the scene.
    directorScript: z.string().max(4000).optional(),
    directorMemory: z.array(z.string().max(200)).max(30).optional(),
});
/** Update body — partial; id comes from the path, owner is immutable. */
export const StageSceneUpdateBodySchema = StageSceneCreateBodySchema.partial();
/** Presigned-upload request for an operator-supplied room file. */
export const StageUploadUrlBodySchema = z.object({
    /** Logical asset role — drives the allowed content-type set. */
    role: z.enum(['panorama', 'backdrop', 'room-glb']),
    contentType: z.string().min(1).max(80),
    /** Original filename (extension used for the S3 key). */
    filename: z.string().min(1).max(200),
});
/** Content-types accepted per upload role (validated server-side). */
export const STAGE_UPLOAD_CONTENT_TYPES = {
    panorama: ['image/jpeg', 'image/png', 'image/webp'],
    backdrop: ['image/jpeg', 'image/png', 'image/webp'],
    'room-glb': ['model/gltf-binary', 'application/octet-stream'],
};
/**
 * Action commands the Stage executes. This is the vocabulary the AI
 * auto-livestream (Phase 8.6) emits via Structured Output — the LLM
 * "thinks" and returns a sequence of these so the character acts while
 * speaking. Also driven manually by the director console.
 *
 * Discriminated on `type`:
 *   - moveTo  : walk to a named anchor OR a raw ground (x, z).
 *   - face    : turn to a heading (degrees).
 *   - playClip: play a looping animation clip by name.
 *   - say     : show the speech bubble (and, later, speak via TTS).
 *   - wait    : pause the command queue for `ms`.
 */
export const StageActionCommandSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('moveTo'),
        anchorId: z.string().max(64).optional(),
        /** stage-broadcast-space — move to a named zone (主播區/熱舞區). The
         *  engine walks to the nearest legal point inside the zone. */
        zoneId: z.string().max(64).optional(),
        x: z.number().optional(),
        z: z.number().optional(),
    }),
    z.object({ type: z.literal('face'), deg: z.number().min(-180).max(180) }),
    z.object({ type: z.literal('playClip'), clip: z.string().min(1).max(80) }),
    z.object({ type: z.literal('say'), text: z.string().min(1).max(300), holdMs: z.number().min(0).max(60000).optional() }),
    z.object({ type: z.literal('wait'), ms: z.number().min(0).max(60000) }),
    // ai-live-director 2.5 — camera language: cut to a saved preset.
    z.object({ type: z.literal('cameraPreset'), id: z.string().min(1).max(64) }),
    // Y-axis dynamics — a ballistic hop (the engine animates v0/gravity;
    // strength 0.5–1.5 scales take-off velocity, default 1).
    z.object({ type: z.literal('jump'), strength: z.number().min(0.5).max(1.5).optional() }),
    // stage-character-fidelity — the character's texture-pack expressions
    // (the SAME vocabulary the chat LLM uses) become stage commands.
    z.object({ type: z.literal('setExpression'), id: z.string().min(1).max(40) }),
    // stage-character-fidelity — show/hide an ai-controlled 黏貼物件
    // (sceneObjects with visibility:'ai-controlled'), same as chat.
    z.object({ type: z.literal('toggleObject'), id: z.string().min(1).max(64), visible: z.boolean() }),
]);
/** A batch of commands (one "beat" of the performance). */
export const StageActionPlanSchema = z.object({
    commands: z.array(StageActionCommandSchema).max(32),
});
/**
 * JSON Schema for the LLM's Structured-Output response_format (8.6) —
 * the auto-livestream director must return `{ commands: StageActionCommand[] }`.
 * Kept as a plain object (not derived from zod) so it can be handed
 * straight to an OpenAI-style `response_format: { type: 'json_schema' }`
 * or an Anthropic tool input_schema. Validate the model's reply with
 * `StageActionPlanSchema.parse` after.
 */
export const STAGE_ACTION_PLAN_JSON_SCHEMA = {
    type: 'object',
    additionalProperties: false,
    required: ['commands'],
    properties: {
        commands: {
            type: 'array',
            maxItems: 32,
            items: {
                type: 'object',
                properties: {
                    type: { type: 'string', enum: ['moveTo', 'face', 'playClip', 'say', 'wait', 'cameraPreset', 'jump', 'setExpression', 'toggleObject'] },
                    anchorId: { type: 'string', description: 'for moveTo: target anchor id' },
                    zoneId: { type: 'string', description: 'for moveTo: target named broadcast zone id (主播區/熱舞區…) — prefer this for purposeful movement' },
                    id: { type: 'string', description: 'for cameraPreset: preset id / for setExpression: expression id / for toggleObject: object id' },
                    visible: { type: 'boolean', description: 'for toggleObject: show (true) or hide (false)' },
                    strength: { type: 'number', description: 'for jump: 0.5-1.5 take-off scale (default 1)' },
                    x: { type: 'number', description: 'for moveTo: raw ground X (use anchorId when possible)' },
                    z: { type: 'number', description: 'for moveTo: raw ground Z' },
                    deg: { type: 'number', description: 'for face: heading -180..180' },
                    clip: { type: 'string', description: 'for playClip: animation clip / action name' },
                    text: { type: 'string', description: 'for say: the spoken line' },
                    holdMs: { type: 'number', description: 'for say: bubble hold ms' },
                    ms: { type: 'number', description: 'for wait: pause ms' },
                },
                required: ['type'],
            },
        },
    },
};
/**
 * Build the system prompt that turns an LLM into the stage director.
 * Lists the room's anchors + the character's clip vocabulary so the model
 * emits valid `moveTo`/`playClip` commands. The caller appends the
 * persona + the current situation/user message as the user turn.
 */
export function buildStageDirectorSystemPrompt(opts) {
    const anchorList = opts.anchors.length
        ? opts.anchors.map((a) => `  - "${a.id}" (${a.label})`).join('\n')
        : '  (none defined)';
    const clipList = (opts.clips ?? []).length ? (opts.clips ?? []).join(', ') : '(none)';
    const actionList = (opts.actions ?? []).length ? (opts.actions ?? []).join(', ') : '(none)';
    return [
        `You are the stage director for a 3D virtual character${opts.characterName ? ` named ${opts.characterName}` : ''} performing a live stream.`,
        'On each turn, decide what the character DOES and SAYS, and return ONLY a JSON object',
        'matching the provided schema: { "commands": StageActionCommand[] }.',
        '',
        'Command types:',
        '  - moveTo: { type:"moveTo", anchorId } — walk to a named spot in the room.',
        '  - face:   { type:"face", deg } — turn to a heading (-180..180).',
        '  - playClip:{ type:"playClip", clip } — play an animation / action.',
        '  - say:    { type:"say", text, holdMs? } — speak a short line (shown as a bubble).',
        '  - wait:   { type:"wait", ms } — pause before the next command.',
        '',
        'Available anchors (use these ids for moveTo):',
        anchorList,
        `Available animation clips: ${clipList}`,
        `Available semantic actions: ${actionList}`,
        '',
        'Guidelines: interleave movement + speech so the character acts WHILE talking',
        '(e.g. moveTo a spot, then say something, then a small wait). Keep lines short and',
        'in-character. Never invent anchor ids or clip names that are not listed. Output JSON only.',
    ].join('\n');
}
/** Label-name convention fallback when a character has no explicit
 *  `model3d.stageClips` slot for a role. Mirrors the design D6 fallback. */
export const STAGE_CLIP_CONVENTION = {
    walk: 'Walk',
    sit: 'Sit_Idle',
    lie: 'Lie_Idle',
    idle: 'Idle',
};
/**
 * Resolve which clip label to play for a locomotion role:
 *   explicit `stageClips[role]` → convention name (if it exists in the
 *   clip set) → null (caller degrades gracefully).
 *
 * @param stageClips the character's `model3d.stageClips` (may be undefined)
 * @param availableClips clip names/labels known to exist on the model
 */
export function resolveStageClip(role, stageClips, availableClips) {
    const explicit = stageClips?.[role];
    if (explicit && availableClips.includes(explicit))
        return explicit;
    if (explicit)
        return explicit; // trust the operator even if we can't see the clip list
    const convention = STAGE_CLIP_CONVENTION[role];
    if (availableClips.includes(convention))
        return convention;
    return null;
}
// ─── ai-live-director — 導播結構化輸出(8.6 的 Cue 層) ────────────────
/** One directed beat from the LLM: spoken line + mood + engine commands
 *  + an optional "worth remembering" memo the console accumulates. */
export const StageDirectorCueSchema = z.object({
    /** 口說台詞 — the console turns this into the leading `say`. */
    line: z.string().min(1).max(300),
    /** Free-form mood word(s) — drives action choice + future tells. */
    mood: z.string().max(40).default(''),
    /** Engine commands for this beat (validated against the catalog by
     *  clampDirectorCue — NOT trusted raw from the model). */
    commands: z.array(StageActionCommandSchema).max(16).default([]),
    /** Something worth remembering across beats (≤200 chars). */
    memo: z.string().max(200).optional(),
    /** Script beat the performance is currently on (run-of-show cursor). */
    beat: z.number().int().min(0).max(99).optional(),
});
/**
 * Lenient cue schema for the LLM call — never rejects a JSON object so one
 * malformed command (or an extra field) can't kill the whole beat. The
 * strict shape is reconstructed by `finalizeDirectorCue`, which drops only
 * the individual bad commands. (The strict schema stays for tests / typing.)
 */
export const StageDirectorCueLenientSchema = z.object({
    line: z.string().optional(),
    mood: z.string().optional(),
    commands: z.array(z.unknown()).optional(),
    memo: z.string().optional(),
    beat: z.union([z.number(), z.string()]).optional(),
}).passthrough();
/**
 * Coerce a loosely-parsed LLM reply into a valid StageDirectorCue:
 *   - unwraps a nested `{ cue: {...} }` the model sometimes adds,
 *   - keeps `line` (trimmed/capped), defaults `mood`,
 *   - filters `commands` to ONLY those that individually pass
 *     StageActionCommandSchema (bad ones dropped, not fatal),
 *   - coerces `beat` from number|string.
 * Pure + exported for tests.
 */
export function finalizeDirectorCue(raw) {
    let o = (raw && typeof raw === 'object') ? raw : {};
    // Some models wrap the answer: { cue: {...} } / { result: {...} }.
    if (!('line' in o) && !('commands' in o)) {
        const nested = (o.cue ?? o.result ?? o.output);
        if (nested && typeof nested === 'object')
            o = nested;
    }
    const line = typeof o.line === 'string' ? o.line.trim().slice(0, 300) : '';
    const mood = typeof o.mood === 'string' ? o.mood.slice(0, 40) : '';
    const rawCmds = Array.isArray(o.commands) ? o.commands : [];
    const commands = [];
    for (const c of rawCmds.slice(0, 32)) {
        const r = StageActionCommandSchema.safeParse(c);
        if (r.success)
            commands.push(r.data);
    }
    const beatN = typeof o.beat === 'number' ? o.beat
        : typeof o.beat === 'string' ? Number.parseInt(o.beat, 10) : NaN;
    return {
        line,
        mood,
        commands,
        ...(typeof o.memo === 'string' && o.memo ? { memo: o.memo.slice(0, 200) } : {}),
        ...(Number.isFinite(beatN) ? { beat: Math.max(0, Math.min(99, beatN)) } : {}),
    };
}
/** What the stage ACTUALLY offers right now — the LLM may only use
 *  these; clampDirectorCue enforces it server-side. */
export const StageDirectorCatalogSchema = z.object({
    anchors: z.array(z.object({
        id: z.string().max(64),
        label: z.string().max(80),
        clip: z.string().max(80).optional(),
    })).max(64).default([]),
    /** Semantic actions: name (操作員定義的名詞) → clip.
     *  stage-character-fidelity — `kind` distinguishes the operator's
     *  循環 (default-loop) vs 一次性 (one-shot, plays `loops`× then
     *  auto-returns to the default loop) so the LLM AND the engine treat
     *  them exactly like Preview/chat do. Absent = legacy loop. */
    actions: z.array(z.object({
        name: z.string().max(80),
        clip: z.string().max(80),
        kind: z.enum(['loop', 'oneshot']).optional(),
        loops: z.number().int().min(1).max(3).optional(),
    })).max(64).default([]),
    /** Raw clip names on the model (fallback vocabulary). */
    clipNames: z.array(z.string().max(80)).max(128).default([]),
    cameraPresets: z.array(z.object({
        id: z.string().max(64),
        label: z.string().max(80),
    })).max(32).default([]),
    /** stage-character-fidelity — the character's texture-pack expressions
     *  with the operator's 「何時用」descriptions (expressionDescriptions)
     *  and auto-return durations. Same vocabulary the chat LLM gets. */
    expressions: z.array(z.object({
        id: z.string().max(40),
        description: z.string().max(200).optional(),
        durationS: z.number().min(0).max(10).optional(),
    })).max(64).default([]),
    /** stage-character-fidelity — ai-controlled 黏貼物件 the LLM may
     *  show/hide via toggleObject (sceneObjects with visibility:'ai-controlled'). */
    objects: z.array(z.object({
        id: z.string().max(64),
        label: z.string().max(80).optional(),
        description: z.string().max(200).optional(),
    })).max(16).default([]),
    /** stage-broadcast-space — named zones the director moves to with
     *  purpose (主播區/熱舞區). `moveTo.zoneId` is clamped against these. */
    zones: z.array(z.object({
        id: z.string().max(64),
        label: z.string().max(40),
        intent: z.string().max(120).optional(),
    })).max(16).default([]),
    /** stage-broadcast-space — the zone the character is CURRENTLY standing
     *  in (if any), so the director reasons about where it already is. */
    currentZone: z.object({ id: z.string().max(64), label: z.string().max(40) }).optional(),
    position: z.object({ x: z.number(), z: z.number() }).optional(),
    mood: z.string().max(40).optional(),
});
/**
 * Hallucination guard — filter a model cue down to what the stage can
 * actually do. Pure (unit-tested):
 *   - moveTo by anchorId: unknown id → command dropped; raw (x,z)
 *     moveTo passes through (the engine clamps into the walkable).
 *   - playClip: unknown clip → try semantic-action name → still
 *     unknown → dropped.
 *   - cameraPreset: unknown id → dropped (camera language is opt-in by
 *     simply offering presets in the catalog).
 *   - wait: ms clamped to ≤15000.
 *   - commands hard-capped at 16 (schema) — defensive re-slice here.
 */
export function clampDirectorCue(cue, catalog) {
    return clampDirectorCueDetailed(cue, catalog).cue;
}
/** Detailed variant of `clampDirectorCue` — same filtering, but also
 *  reports every dropped command with its reason (beat-trace clamp node). */
export function clampDirectorCueDetailed(cue, catalog) {
    const anchorIds = new Set(catalog.anchors.map((a) => a.id));
    const zoneIds = new Set(catalog.zones.map((z) => z.id));
    const clipSet = new Set(catalog.clipNames);
    for (const a of catalog.actions)
        clipSet.add(a.clip);
    const actionByName = new Map(catalog.actions.map((a) => [a.name, a.clip]));
    const commands = [];
    const drops = [];
    for (const cmd of cue.commands.slice(0, 16)) {
        if (cmd.type === 'moveTo') {
            // stage-broadcast-space — zone target wins (purposeful movement).
            if (cmd.zoneId !== undefined) {
                if (zoneIds.has(cmd.zoneId))
                    commands.push(cmd);
                else
                    drops.push({ command: cmd, reason: `moveTo: 區域 "${cmd.zoneId}" 不存在` });
                continue;
            }
            if (cmd.anchorId !== undefined) {
                if (anchorIds.has(cmd.anchorId))
                    commands.push(cmd);
                else
                    drops.push({ command: cmd, reason: `moveTo: 錨點 "${cmd.anchorId}" 不存在` });
                continue;
            }
            if (typeof cmd.x === 'number' && typeof cmd.z === 'number')
                commands.push(cmd);
            else
                drops.push({ command: cmd, reason: 'moveTo: 缺 zoneId/anchorId 也缺 (x,z)' });
            continue;
        }
        if (cmd.type === 'playClip') {
            if (clipSet.has(cmd.clip)) {
                commands.push(cmd);
                continue;
            }
            const mapped = actionByName.get(cmd.clip);
            if (mapped) {
                commands.push({ ...cmd, clip: mapped });
                continue;
            }
            drops.push({ command: cmd, reason: `playClip: "${cmd.clip}" 不在動作語彙` });
            continue;
        }
        if (cmd.type === 'cameraPreset') {
            if (catalog.cameraPresets.some((c) => c.id === cmd.id))
                commands.push(cmd);
            else
                drops.push({ command: cmd, reason: `cameraPreset: "${cmd.id}" 不存在` });
            continue;
        }
        if (cmd.type === 'setExpression') {
            if (catalog.expressions.some((x) => x.id === cmd.id))
                commands.push(cmd);
            else
                drops.push({ command: cmd, reason: `setExpression: 表情 "${cmd.id}" 不在角色表情包` });
            continue;
        }
        if (cmd.type === 'toggleObject') {
            if (catalog.objects.some((o) => o.id === cmd.id))
                commands.push(cmd);
            else
                drops.push({ command: cmd, reason: `toggleObject: 物件 "${cmd.id}" 不是 AI 可控物件` });
            continue;
        }
        if (cmd.type === 'wait') {
            commands.push({ ...cmd, ms: Math.min(cmd.ms, 15000) });
            continue;
        }
        commands.push(cmd); // face / say / jump pass through (schema-bounded)
    }
    return { cue: { ...cue, commands }, drops };
}
/** JSON Schema handed to the LLM as response_format for a director cue. */
export const STAGE_DIRECTOR_CUE_JSON_SCHEMA = {
    type: 'object',
    additionalProperties: false,
    required: ['line', 'mood', 'commands'],
    properties: {
        line: { type: 'string', description: '本拍的口說台詞(自然口語,銜接前文)' },
        mood: { type: 'string', description: '當下情緒,1-3 個詞' },
        commands: STAGE_ACTION_PLAN_JSON_SCHEMA.properties.commands,
        memo: { type: 'string', description: '(可選)值得長期記住的事,≤200字' },
        beat: { type: 'number', description: '(可選)目前所在的腳本段號' },
    },
};
// ─── director-trace — 每一拍的流程圖式追蹤事件 ───────────────────────
/**
 * One node in a beat's pipeline trace (目錄組裝 → Prompt → LLM → 驗證 →
 * Clamp → 逐指令執行). Generic on purpose — the same shape covers the
 * chat pipeline's STT→LLM→TTS later. Rendered by the console's 🔬 flow
 * drawer as a horizontal node graph (green ok / yellow warn / red error).
 */
export const StageTraceEventSchema = z.object({
    /** Node id, e.g. 'catalog' | 'brain' | 'prompt' | 'llm' | 'validate'
     *  | 'clamp' | 'dispatch' | 'tts' | 'cmd:moveTo' … */
    node: z.string().max(40),
    status: z.enum(['ok', 'warn', 'error']),
    /** ms offset from the beat's t0 (server or console epoch — per side). */
    startMs: z.number().min(0),
    durMs: z.number().min(0),
    /** One-line human summary shown on the node box. */
    summary: z.string().max(200),
    /** Full payload for the expanded view (prompt fulltext, raw LLM JSON,
     *  clamp diff …). Kept as a string so the wire shape stays simple. */
    detail: z.string().max(20000).optional(),
});
// ─── stage-character-fidelity — 角色完整設定進舞台 ───────────────────
/**
 * The character-record slice the console sends INTO the stage window so
 * the stage mounts EXACTLY like Preview / the fan page. Display-only
 * fields — deliberately NO `character.ai` (the brain stays server-side
 * behind /direct) and NO raw voice credentials (TTS synth happens on
 * the authed console; the stage only receives audio buffers).
 *
 * `model3d` is passed through loosely (z.record) because the stage just
 * forwards it to the SAME controller + `applyPersistedModel3DConfig`
 * pipeline Preview uses — re-declaring every field here would create a
 * second schema to drift. The authoritative shape is `character.ts`.
 */
export const StageCharacterPayloadSchema = z.object({
    characterId: z.string().min(1).max(64),
    name: z.string().max(80).default(''),
    modelPath: z.string().url(),
    modelFormat: z.enum(['glb', 'fbx']).optional(),
    /** Public-safe TTS provider flag (same enum as public-info). The
     *  console synthesises through the authed character-keyed proxy. */
    voiceProvider: z.enum(['browser', 'voxcpm', 'voai', 'brainserver']).optional(),
    /** The character's full `model3d` block (texturePack, clipLabels,
     *  defaultClipLabels, oneShotClipLabels(+loopCounts), renderParams,
     *  viewportPose, sceneObjects, expressions, proceduralActions …). */
    model3d: z.record(z.string(), z.unknown()).default({}),
});
// ─── Built-in scene templates(純 3D WebGL,開放式棚景) ──────────────
/**
 * 內建範本:錄音棚 — OPEN broadcast-studio set (no enclosing walls);
 * `builtin:recording-studio` renders procedurally in stage-runtime.
 * 16m-radius floor, freestanding acoustic backdrop, overhead truss.
 */
export const RECORDING_STUDIO_SCENE = {
    id: 'builtin-recording-studio',
    name: '錄音棚(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:recording-studio' },
    // Open set: a 16m-radius floor; walkable is a generous 16×11.5m
    // apron in front of the freestanding backdrop (z=-6) — cameras live
    // in the open +z space and always see in.
    walkable: { polygon: [[-8, -4.4], [8, -4.4], [8, 7], [-8, 7]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-desk', x: 5.2, z: -0.5, radius: 1.3 },
        { id: 'obs-mic', x: 0.7, z: -2.2, radius: 0.35 },
        { id: 'obs-couch', x: -5.2, z: 0.2, radius: 1.4 },
        { id: 'obs-tree-l', x: -7, z: -1, radius: 0.5 },
        { id: 'obs-tree-r', x: 7, z: -1, radius: 0.5 },
        { id: 'obs-post-l', x: -5, z: 3, radius: 0.3 },
        { id: 'obs-post-r', x: 5, z: 3, radius: 0.3 },
    ],
    anchors: [
        { id: 'mic', label: '麥克風前', worldPos: [0, 0, -1.5], clip: 'Idle', facing: 0 },
        { id: 'console', label: '控台旁', worldPos: [3.8, 0, -0.5], clip: 'Idle', facing: 95 },
        { id: 'couch', label: '沙發訪談區', worldPos: [-3.7, 0, 0.9], clip: 'Idle', facing: -70 },
        { id: 'center', label: '舞台中央', worldPos: [0, 0, 1.5], clip: 'Idle', facing: 0 },
        { id: 'monitors', label: '監聽喇叭區', worldPos: [-2.2, 0, -3.6], clip: 'Idle', facing: -20 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '正面全景', position: [0, 2.4, 8.5], lookAt: [0, 1.3, -1.5], isDefault: true },
        { id: 'cam-mic', label: '麥克風特寫', position: [0.3, 1.5, 0.8], lookAt: [0, 1.35, -1.9], isDefault: false },
        { id: 'cam-desk', label: '控台側寫', position: [2.0, 1.8, 2.8], lookAt: [4.8, 1.0, -0.6], isDefault: false },
        { id: 'cam-couch', label: '沙發訪談', position: [-2.4, 1.6, 3.4], lookAt: [-4.8, 0.9, 0.2], isDefault: false },
        {
            id: 'cam-orbit', label: '環繞運鏡', position: [7, 2.6, 5], lookAt: [0, 1.2, -1],
            isDefault: false, motion: { kind: 'orbit', speed: 3 },
        },
    ],
    objects: [],
    zones: [],
};
// ─── Per-show built-in sets — 每個節目自己的棚 ───────────────────────
// Every obstacle circle below matches a furniture piece the
// stage-runtime builder places at the SAME coordinates — the Navigator
// treats them as solid, so the character cannot walk through the radio
// table, the news desk or the night-market stall.
/** 📻 深夜電台 — 圓桌+檯燈+唱片牆+夜窗+燈串。 */
export const RADIO_STUDIO_SCENE = {
    id: 'builtin-radio-studio',
    name: '深夜電台(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:radio-studio' },
    walkable: { polygon: [[-7, -4], [7, -4], [7, 6], [-7, 6]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-table', x: 0, z: -1.5, radius: 1.0 },
        { id: 'obs-shelf-l', x: -2.5, z: -4.8, radius: 1.2 },
        { id: 'obs-shelf-c', x: 0, z: -4.8, radius: 1.2 },
        { id: 'obs-shelf-r', x: 2.5, z: -4.8, radius: 1.2 },
        { id: 'obs-window', x: -4.5, z: -2.5, radius: 0.8 },
    ],
    anchors: [
        { id: 'table', label: '主持桌', worldPos: [0, 0, -0.3], clip: 'Idle', facing: 0 },
        { id: 'window', label: '夜窗邊', worldPos: [-3.4, 0, -1.6], clip: 'Idle', facing: -45 },
        { id: 'shelf', label: '唱片牆', worldPos: [2.8, 0, -3.4], clip: 'Idle', facing: 20 },
        { id: 'center', label: '舞台中央', worldPos: [0, 0, 1.5], clip: 'Idle', facing: 0 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '正面全景', position: [0, 2.0, 7], lookAt: [0, 1.2, -1.2], isDefault: true },
        { id: 'cam-table', label: '主持桌特寫', position: [0.4, 1.4, 1.4], lookAt: [0, 1.2, -1.2], isDefault: false },
        { id: 'cam-orbit', label: '環繞運鏡', position: [6, 2.2, 4], lookAt: [0, 1.1, -1], isDefault: false, motion: { kind: 'orbit', speed: 3 } },
    ],
    objects: [],
    zones: [],
};
/** 🌤 天氣預報台 — 大天空螢幕+主播講台+漂浮雲朵。 */
export const WEATHER_STUDIO_SCENE = {
    id: 'builtin-weather-studio',
    name: '天氣預報台(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:weather-studio' },
    walkable: { polygon: [[-7, -3.8], [7, -3.8], [7, 6], [-7, 6]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-screen-l', x: -3.3, z: -4.8, radius: 1.4 },
        { id: 'obs-screen-c', x: 0, z: -4.8, radius: 1.4 },
        { id: 'obs-screen-r', x: 3.3, z: -4.8, radius: 1.4 },
        { id: 'obs-podium', x: 2.2, z: -2, radius: 0.7 },
    ],
    anchors: [
        { id: 'screen', label: '天氣圖前', worldPos: [-2, 0, -3], clip: 'Idle', facing: -30 },
        { id: 'podium', label: '主播講台', worldPos: [2.2, 0, -1.1], clip: 'Idle', facing: 10 },
        { id: 'center', label: '舞台中央', worldPos: [0, 0, 0], clip: 'Idle', facing: 0 },
        { id: 'front', label: '鏡頭前', worldPos: [0, 0, 2], clip: 'Idle', facing: 0 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '正面全景', position: [0, 2.2, 7.5], lookAt: [0, 1.3, -2], isDefault: true },
        { id: 'cam-screen', label: '天氣圖帶位', position: [2.5, 1.8, 2], lookAt: [-2, 1.4, -4], isDefault: false },
        { id: 'cam-orbit', label: '環繞運鏡', position: [6.5, 2.4, 4.5], lookAt: [0, 1.2, -2], isDefault: false, motion: { kind: 'orbit', speed: 3 } },
    ],
    objects: [],
    zones: [],
};
/** 🗞 新聞台 — 主播桌+LIVE 側板+光條牆。主播位在桌後。 */
export const NEWS_DESK_SCENE = {
    id: 'builtin-news-desk',
    name: '新聞台(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:news-desk' },
    walkable: { polygon: [[-7, -4.6], [7, -4.6], [7, 6], [-7, 6]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-desk', x: 0, z: -1.9, radius: 1.5 },
        { id: 'obs-panel-l', x: -4, z: -4, radius: 0.9 },
        { id: 'obs-panel-r', x: 4, z: -4, radius: 0.9 },
        { id: 'obs-wall-l', x: -2.7, z: -5.2, radius: 1.3 },
        { id: 'obs-wall-c', x: 0, z: -5.2, radius: 1.3 },
        { id: 'obs-wall-r', x: 2.7, z: -5.2, radius: 1.3 },
    ],
    anchors: [
        { id: 'desk', label: '主播位(桌後)', worldPos: [0, 0, -3.6], clip: 'Idle', facing: 0 },
        { id: 'front', label: '桌前站位', worldPos: [0, 0, 0.5], clip: 'Idle', facing: 0 },
        { id: 'panel-l', label: '左側 LIVE 板', worldPos: [-3.2, 0, -2.6], clip: 'Idle', facing: -40 },
        { id: 'panel-r', label: '右側 LIVE 板', worldPos: [3.2, 0, -2.6], clip: 'Idle', facing: 40 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '主播正面', position: [0, 1.9, 4.5], lookAt: [0, 1.35, -3.6], isDefault: true },
        { id: 'cam-wide', label: '全景', position: [0, 2.6, 8], lookAt: [0, 1.2, -2], isDefault: false },
        { id: 'cam-orbit', label: '環繞運鏡', position: [6.5, 2.4, 4], lookAt: [0, 1.2, -2.5], isDefault: false, motion: { kind: 'orbit', speed: 3 } },
    ],
    objects: [],
    zones: [],
};
/** 🍜 夜市攤 — 棚架燈籠+攤台+蒸籠+菜單板+紅凳。 */
export const FOOD_MARKET_SCENE = {
    id: 'builtin-food-market',
    name: '夜市攤(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:food-market' },
    walkable: { polygon: [[-7, -3], [7, -3], [7, 6], [-7, 6]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-counter', x: 0, z: -2.5, radius: 1.3 },
        { id: 'obs-back', x: 0, z: -3.6, radius: 1.2 },
        { id: 'obs-post-l', x: -1.7, z: -1.7, radius: 0.3 },
        { id: 'obs-post-r', x: 1.7, z: -1.7, radius: 0.3 },
        { id: 'obs-menu', x: 3.4, z: -2.2, radius: 0.7 },
        { id: 'obs-stool-1', x: -3, z: 0.5, radius: 0.45 },
        { id: 'obs-stool-2', x: -3.9, z: -0.6, radius: 0.45 },
    ],
    anchors: [
        { id: 'stall', label: '攤位前', worldPos: [0, 0, -0.9], clip: 'Idle', facing: 0 },
        { id: 'menu', label: '菜單板旁', worldPos: [2.6, 0, -1.2], clip: 'Idle', facing: 30 },
        { id: 'stool', label: '客人座位區', worldPos: [-2.9, 0, 1.2], clip: 'Idle', facing: -20 },
        { id: 'center', label: '走道中央', worldPos: [0, 0, 2], clip: 'Idle', facing: 0 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '攤位正面', position: [0, 2.0, 6.5], lookAt: [0, 1.2, -1.5], isDefault: true },
        { id: 'cam-food', label: '攤台特寫', position: [1.2, 1.4, 0.6], lookAt: [0, 1.0, -2.4], isDefault: false },
        { id: 'cam-orbit', label: '環繞運鏡', position: [6, 2.2, 4], lookAt: [0, 1.1, -1.5], isDefault: false, motion: { kind: 'orbit', speed: 3 } },
    ],
    objects: [],
    zones: [],
};
/** ☕ 咖啡廳 — 吧台+書牆+兩張小圓桌+吊燈+窗。 */
export const CHAT_CAFE_SCENE = {
    id: 'builtin-chat-cafe',
    name: '咖啡廳(內建範本)',
    ownerCustomerId: 'builtin',
    kind: '3d-room',
    room3d: { glbUrl: 'builtin:chat-cafe' },
    walkable: { polygon: [[-6.5, -3.8], [6.5, -3.8], [6.5, 6], [-6.5, 6]] },
    groundY: 0,
    obstacles: [
        { id: 'obs-bar', x: -3.5, z: -3, radius: 1.5 },
        { id: 'obs-shelf-l', x: -2.6, z: -4.9, radius: 1.3 },
        { id: 'obs-shelf-c', x: 0, z: -4.9, radius: 1.3 },
        { id: 'obs-shelf-r', x: 2.6, z: -4.9, radius: 1.3 },
        { id: 'obs-table-1', x: 2.2, z: -1, radius: 0.8 },
        { id: 'obs-table-2', x: -0.8, z: 1.6, radius: 0.8 },
        { id: 'obs-window', x: 4.8, z: -3, radius: 0.8 },
    ],
    anchors: [
        { id: 'bar', label: '吧台邊', worldPos: [-2.2, 0, -1.8], clip: 'Idle', facing: -60 },
        { id: 'table', label: '圓桌旁', worldPos: [2.2, 0, 0.1], clip: 'Idle', facing: 10 },
        { id: 'window', label: '窗邊', worldPos: [3.8, 0, -1.8], clip: 'Idle', facing: 60 },
        { id: 'center', label: '店中央', worldPos: [0.5, 0, 3], clip: 'Idle', facing: 0 },
    ],
    cameraPresets: [
        { id: 'cam-main', label: '店內全景', position: [0, 2.0, 7], lookAt: [0, 1.2, -1.5], isDefault: true },
        { id: 'cam-table', label: '圓桌特寫', position: [3.4, 1.4, 1.6], lookAt: [2.0, 1.1, -1.0], isDefault: false },
        { id: 'cam-orbit', label: '環繞運鏡', position: [5.5, 2.2, 4.5], lookAt: [0, 1.1, -1], isDefault: false, motion: { kind: 'orbit', speed: 3 } },
    ],
    objects: [],
    zones: [],
};
/** showId(stage-shows preset)→ its built-in set. free-talk keeps the
 *  original recording studio. */
export const SHOW_SCENE_TEMPLATES = {
    'free-talk': RECORDING_STUDIO_SCENE,
    'chat-cafe': CHAT_CAFE_SCENE,
    'weather-station': WEATHER_STUDIO_SCENE,
    'news-desk': NEWS_DESK_SCENE,
    'taiwan-food': FOOD_MARKET_SCENE,
    'night-radio': RADIO_STUDIO_SCENE,
};
